/* src/utils/kbAssemble.js
 * Deterministic guided KB assembly (no AI, no writes).
 * GUIDED_SECTION_LABELS is the single source of truth for assembler + parser.
 */

export const GUIDED_SECTION_LABELS = [
  'Overview:',
  'What we offer:',
  'Pricing:',
  'Area and hours:',
  'Voice and tone:',
  'About us:',
  'Notable and news:',
  'FAQ:',
  'Booking / next step:',
  'Do not say:',
  'Reference material:',
  'Sources:',
];

export const TONE_CHIPS = ['Friendly', 'Professional', 'Playful', 'Direct', 'Warm'];

const GUIDED_FIELD_COUNT = 11;

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyGuidedAnswers() {
  return {
    overview: '',
    offerings: '',
    pricing: '',
    areaAndHours: '',
    tone: '',
    about: '',
    notable: '',
    faq: [{ id: newId(), question: '', answer: '' }],
    referenceLinks: [],
    booking: '',
    guardrails: '',
  };
}

function appendSection(parts, label, content) {
  const trimmed = (content || '').trim();
  if (!trimmed) return;
  parts.push(`${label}\n${trimmed}`);
}

function formatFaq(faq) {
  if (!Array.isArray(faq)) return '';
  const pairs = faq
    .filter((row) => row.question?.trim() && row.answer?.trim())
    .map((row) => `Q: ${row.question.trim()}\nA: ${row.answer.trim()}`);
  return pairs.join('\n\n');
}

function formatReferenceMaterial(referenceLinks) {
  if (!Array.isArray(referenceLinks)) return '';
  return referenceLinks
    .map((link) => link.pulledContent?.trim())
    .filter(Boolean)
    .join('\n');
}

function formatSources(referenceLinks) {
  if (!Array.isArray(referenceLinks)) return '';
  const urls = referenceLinks.map((link) => link.url?.trim()).filter(Boolean);
  return urls.join(', ');
}

export function assembleGuidedContent(answers) {
  if (!answers) return '';

  const parts = [];

  appendSection(parts, 'Overview:', answers.overview);
  appendSection(parts, 'What we offer:', answers.offerings);
  appendSection(parts, 'Pricing:', answers.pricing);
  appendSection(parts, 'Area and hours:', answers.areaAndHours);
  appendSection(parts, 'Voice and tone:', answers.tone);
  appendSection(parts, 'About us:', answers.about);
  appendSection(parts, 'Notable and news:', answers.notable);

  const faqText = formatFaq(answers.faq);
  appendSection(parts, 'FAQ:', faqText);

  appendSection(parts, 'Booking / next step:', answers.booking);
  appendSection(parts, 'Do not say:', answers.guardrails);

  const referenceMaterial = formatReferenceMaterial(answers.referenceLinks);
  appendSection(parts, 'Reference material:', referenceMaterial);

  const sources = formatSources(answers.referenceLinks);
  appendSection(parts, 'Sources:', sources);

  return parts.join('\n\n');
}

function fieldAnswered(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

export function countAnsweredFields(answers) {
  if (!answers) return 0;

  let count = 0;
  if (fieldAnswered(answers.overview)) count += 1;
  if (fieldAnswered(answers.offerings)) count += 1;
  if (fieldAnswered(answers.pricing)) count += 1;
  if (fieldAnswered(answers.areaAndHours)) count += 1;
  if (fieldAnswered(answers.tone)) count += 1;
  if (fieldAnswered(answers.about)) count += 1;
  if (fieldAnswered(answers.notable)) count += 1;

  const hasFaq = Array.isArray(answers.faq)
    && answers.faq.some((row) => fieldAnswered(row.question) && fieldAnswered(row.answer));
  if (hasFaq) count += 1;

  const hasLinks = Array.isArray(answers.referenceLinks)
    && answers.referenceLinks.some((link) => fieldAnswered(link.url));
  if (hasLinks) count += 1;

  if (fieldAnswered(answers.booking)) count += 1;
  if (fieldAnswered(answers.guardrails)) count += 1;

  return count;
}

export function guidedFieldTotal() {
  return GUIDED_FIELD_COUNT;
}

export function hasGuidedContent(answers) {
  return assembleGuidedContent(answers).trim().length > 0;
}

export function guidedDisplayLabel(label) {
  if (!label) return '';
  return label.endsWith(':') ? label.slice(0, -1) : label;
}
